import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material'
import {
  Close,
  ExpandMore,
  BugReport,
  AccountTree,
  Description,
  Settings,
} from '@mui/icons-material'
import { useSessionStore } from '../../stores/sessionStore'
import { SessionStatus, SessionNodeStatus, JournalEntryType } from '@api/types/session'
import { WorkflowNodeType } from '../../types/workflow.types'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

// Format JSON with syntax highlighting
function JsonView({ data, maxHeight = 400 }: { data: unknown; maxHeight?: number }) {
  const jsonString = JSON.stringify(data, null, 2)
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        maxHeight,
        overflow: 'auto',
        bgcolor: 'grey.900',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: 'grey.100',
      }}
    >
      {jsonString}
    </Paper>
  )
}

// Status chip color mapping
function getStatusColor(status: SessionStatus): 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' {
  switch (status) {
    case SessionStatus.NotStarted:
      return 'default'
    case SessionStatus.InProgress:
      return 'primary'
    case SessionStatus.Paused:
      return 'warning'
    case SessionStatus.Completed:
      return 'success'
    case SessionStatus.Failed:
    case SessionStatus.Abandoned:
      return 'error'
    default:
      return 'default'
  }
}

function getStatusLabel(status: SessionStatus): string {
  switch (status) {
    case SessionStatus.NotStarted:
      return 'Not Started'
    case SessionStatus.InProgress:
      return 'In Progress'
    case SessionStatus.Paused:
      return 'Paused'
    case SessionStatus.Completed:
      return 'Completed'
    case SessionStatus.Failed:
      return 'Failed'
    case SessionStatus.Abandoned:
      return 'Abandoned'
    default:
      return `Unknown (${status})`
  }
}

function getEntryTypeName(type: JournalEntryType): string {
  return JournalEntryType[type] || `Unknown (${type})`
}

function getNodeStatusLabel(status: SessionNodeStatus): string {
  switch (status) {
    case SessionNodeStatus.Pending:
      return 'Pending'
    case SessionNodeStatus.AwaitingInput:
      return 'Awaiting Input'
    case SessionNodeStatus.AwaitingVote:
      return 'Awaiting Vote'
    case SessionNodeStatus.Processing:
      return 'Processing'
    case SessionNodeStatus.Completed:
      return 'Completed'
    case SessionNodeStatus.Waiting:
      return 'Waiting'
    case SessionNodeStatus.OnCooldown:
      return 'On Cooldown'
    default:
      return `Unknown (${status})`
  }
}

interface SessionDebugPanelProps {
  open: boolean
  onClose: () => void
}

export default function SessionDebugPanel({ open, onClose }: SessionDebugPanelProps) {
  const [tabValue, setTabValue] = useState(0)
  const store = useSessionStore()

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  // Filter journal to show only path-related entries for flow view
  // Exclude Decision nodes (type 2) as they are internal routing nodes
  const flowEntries = store.journal.filter((e) => {
    // Always include SessionStarted and LoopDetected
    if (e.entryType === JournalEntryType.SessionStarted ||
        e.entryType === JournalEntryType.LoopDetected) {
      return true
    }
    // For PathTaken, exclude entries where target node is a Decision node
    if (e.entryType === JournalEntryType.PathTaken) {
      const content = e.content as Record<string, unknown> | null
      const targetNodeType = content?.targetNodeType as number | undefined
      // Hide Decision nodes (type 2) from the flow list
      if (targetNodeType === WorkflowNodeType.Decision) {
        return false
      }
      return true
    }
    return false
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <BugReport color="warning" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          Session Debug Panel
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Settings />} label="State" iconPosition="start" />
          <Tab icon={<AccountTree />} label="Flow" iconPosition="start" />
          <Tab icon={<Description />} label="Journal" iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Session State Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Quick Info */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Session Overview
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip
                    label={getStatusLabel(store.status)}
                    color={getStatusColor(store.status)}
                    size="small"
                  />
                  <Chip
                    label={store.isSolo ? 'Solo' : `Group (${store.participantCount})`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={`${(store.progressPercent ?? 0).toFixed(1)}% Progress`}
                    variant="outlined"
                    size="small"
                  />
                  {store.requiresInput && (
                    <Chip label="Requires Input" color="warning" size="small" />
                  )}
                  {store.isVotingActive && (
                    <Chip label="Voting Active" color="info" size="small" />
                  )}
                  <Chip
                    label={`Node: ${getNodeStatusLabel(store.currentNodeStatus)}`}
                    variant="outlined"
                    size="small"
                    color={store.currentNodeStatus === SessionNodeStatus.OnCooldown ? 'warning' : 'default'}
                  />
                </Box>

                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <strong>Session ID:</strong> {store.sessionId || 'N/A'}
                </Typography>
                {store.cooldownEndsAt && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    <strong>Cooldown Ends:</strong> {new Date(store.cooldownEndsAt.endsWith('Z') ? store.cooldownEndsAt : `${store.cooldownEndsAt}Z`).toLocaleTimeString()}
                  </Typography>
                )}
                {store.waitingReason && (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    <strong>Reason:</strong> {store.waitingReason}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <strong>Adventure ID:</strong> {store.adventureId || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <strong>Started At:</strong> {store.startedAt || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <strong>Elapsed:</strong> {store.elapsedSeconds ?? 0}s
                </Typography>
              </Paper>

              {/* Current Node */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Current Node</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mb: 1 }}>
                    <strong>Node ID:</strong> {store.currentNodeId || 'N/A'}
                  </Typography>
                  <JsonView data={store.currentNode} maxHeight={300} />
                </AccordionDetails>
              </Accordion>

              {/* Pending Input */}
              {store.pendingInput && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">Pending Input</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <JsonView data={store.pendingInput} maxHeight={200} />
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Voting State */}
              {store.voting && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">Voting State</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <JsonView data={store.voting} maxHeight={200} />
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Revealed Locations */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Revealed Locations ({store.revealedLocations.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <JsonView data={store.revealedLocations} maxHeight={200} />
                </AccordionDetails>
              </Accordion>

              {/* Last API Response */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    Last API Response
                    {store.lastStateResponseTimestamp && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: 'text.secondary' }}
                      >
                        ({new Date(store.lastStateResponseTimestamp).toLocaleTimeString()})
                      </Typography>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {store.lastStateResponse ? (
                    <JsonView data={store.lastStateResponse} maxHeight={400} />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No API response received yet.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Full Store State */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">Full Store State (Raw)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <JsonView
                    data={{
                      sessionId: store.sessionId,
                      adventureId: store.adventureId,
                      adventureTitle: store.adventureTitle,
                      status: store.status,
                      currentNodeId: store.currentNodeId,
                      currentNodeStatus: store.currentNodeStatus,
                      cooldownEndsAt: store.cooldownEndsAt,
                      waitingReason: store.waitingReason,
                      requiresInput: store.requiresInput,
                      inputType: store.inputType,
                      progressPercent: store.progressPercent,
                      startedAt: store.startedAt,
                      completedAt: store.completedAt,
                      lastActivityAt: store.lastActivityAt,
                      elapsedSeconds: store.elapsedSeconds,
                      isSolo: store.isSolo,
                      isGroup: store.isGroup,
                      groupId: store.groupId,
                      participantCount: store.participantCount,
                      isVotingActive: store.isVotingActive,
                      isLoading: store.isLoading,
                      isProcessing: store.isProcessing,
                      isSubmitting: store.isSubmitting,
                      error: store.error,
                    }}
                    maxHeight={300}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          </TabPanel>

          {/* Flow Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Workflow Path ({flowEntries.length} steps)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {flowEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No flow steps recorded yet.
                </Typography>
              ) : (
                flowEntries.map((entry) => {
                  // Extract edge/path information from content
                  const content = entry.content as Record<string, unknown> | null
                  const edgeId = content?.edgeId as string | undefined
                  const edgeLabel = content?.edgeLabel as string | undefined
                  const sourceNodeId = content?.sourceNodeId as string | undefined
                  const sourceNodeLabel = content?.sourceNodeLabel as string | undefined
                  const targetNodeId = content?.targetNodeId as string | undefined
                  const targetNodeLabel = content?.targetNodeLabel as string | undefined
                  const branchKey = entry.branchKey || (content?.branchKey as string | undefined)
                  const loopCount = content?.loopCount as number | undefined

                  return (
                    <Paper
                      key={entry.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        bgcolor: entry.entryType === JournalEntryType.LoopDetected
                          ? 'warning.dark'
                          : 'grey.900',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip
                          label={`#${entry.sequenceNumber}`}
                          size="small"
                          sx={{ minWidth: 45 }}
                        />
                        <Chip
                          label={getEntryTypeName(entry.entryType)}
                          size="small"
                          color={entry.entryType === JournalEntryType.LoopDetected ? 'warning' : 'default'}
                          variant="outlined"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {new Date(entry.createdAt).toLocaleTimeString()}
                        </Typography>
                      </Box>

                      {/* Show source → edge → target for PathTaken */}
                      {entry.entryType === JournalEntryType.PathTaken && (
                        <Box sx={{ mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {/* Source Node */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="caption" color="text.secondary">From</Typography>
                              <Chip
                                label={sourceNodeLabel || sourceNodeId || 'Unknown'}
                                size="small"
                                variant="outlined"
                                color="info"
                              />
                            </Box>

                            {/* Arrow and Edge Info */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {edgeLabel || branchKey || 'Edge'}
                              </Typography>
                              <Typography sx={{ color: 'grey.500' }}>→</Typography>
                            </Box>

                            {/* Target Node */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography variant="caption" color="text.secondary">To</Typography>
                              <Chip
                                label={targetNodeLabel || targetNodeId || (content?.nodeLabel as string) || 'Unknown'}
                                size="small"
                                variant="outlined"
                                color="success"
                              />
                            </Box>
                          </Box>

                          {/* Edge ID */}
                          {edgeId && (
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', color: 'grey.500', display: 'block', mt: 0.5 }}
                            >
                              Edge: {edgeId}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Show loop info for LoopDetected */}
                      {entry.entryType === JournalEntryType.LoopDetected && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            <strong>Node:</strong> {(content?.nodeLabel as string) || targetNodeLabel || 'Unknown'}
                          </Typography>
                          {loopCount !== undefined && (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              <strong>Loop Count:</strong> {loopCount}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Show node label for other types */}
                      {entry.entryType === JournalEntryType.SessionStarted && (content?.nodeLabel as string) && (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 1 }}>
                          <strong>Starting Node:</strong> {content?.nodeLabel as string}
                        </Typography>
                      )}

                      {/* Show raw content in expandable section */}
                      {entry.content && Object.keys(entry.content).length > 0 && (
                        <Accordion
                          sx={{
                            mt: 1,
                            bgcolor: 'transparent',
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore sx={{ fontSize: '1rem' }} />}
                            sx={{ minHeight: 'unset', p: 0, '& .MuiAccordionSummary-content': { m: 0 } }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Raw content
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, pt: 1 }}>
                            <JsonView data={entry.content} maxHeight={100} />
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </Paper>
                  )
                })
              )}
            </Box>
          </TabPanel>

          {/* Journal Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              All Journal Entries ({store.journal.length})
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {store.journal.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No journal entries yet.
                </Typography>
              ) : (
                store.journal.map((entry) => (
                  <Accordion key={entry.id}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                        <Chip
                          label={`#${entry.sequenceNumber}`}
                          size="small"
                          sx={{ minWidth: 45 }}
                        />
                        <Chip
                          label={entry.entryTypeName || getEntryTypeName(entry.entryType)}
                          size="small"
                          variant="outlined"
                          color={entry.isSystemGenerated ? 'default' : 'primary'}
                        />
                        {(entry.content as Record<string, unknown> | null)?.nodeLabel && (
                          <Typography
                            variant="body2"
                            sx={{
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {(entry.content as Record<string, unknown>).nodeLabel as string}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {new Date(entry.createdAt).toLocaleTimeString()}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <JsonView data={entry} maxHeight={250} />
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </TabPanel>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
